import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Phone, AlertTriangle, ExternalLink } from 'lucide-react';

const NayziPage = () => {
  const { language } = useLanguage();

  return (
    <main className="pt-20 sm:pt-24">
      {/* Hero */}
      <section className="bg-[#1a1a1a] py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 bg-[#E31B23] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <AlertTriangle className="w-4 h-4" />
              {language === 'fr' ? 'Ressources externes' : 'External Resources'}
            </span>
            <h1 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mb-4">
              {language === 'fr'
                ? 'Ressources officielles du fabricant \u2014 Nayzilam\u00ae'
                : 'Official Manufacturer Resources \u2014 Nayzilam\u00ae'}
            </h1>
            <p className="font-manrope text-neutral-400 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Midazolam en vaporisateur nasal \u2014 Vid\u00e9o publi\u00e9e par le fabricant'
                : 'Midazolam nasal spray \u2014 Video published by the manufacturer'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Video */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-outfit font-bold text-2xl text-[#1a1a1a] mb-6">
              Nayzilam® — 5 mg
            </h2>
            <div className="aspect-video rounded-2xl overflow-hidden bg-neutral-100 shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/8zPn6aOQ7AY"
                title="Nayzilam Instructions for Use"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <a
              href="https://www.nayzilam.com/how-and-when-to-use-nayzilam"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-[#E31B23] hover:text-[#c8171e] font-bold transition-colors"
              data-testid="nayzi-instructions"
            >
              <ExternalLink className="w-4 h-4" />
              <span>
                {language === 'fr'
                  ? 'Lire les instructions \u00e9crites officielles (site du fabricant)'
                  : 'Read official written instructions (Manufacturer website)'}
              </span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Emergency Notice */}
      <section className="py-12 bg-[#E31B23]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Phone className="w-6 h-6 text-white" />
            <p className="font-outfit font-bold text-xl text-white">
              {language === 'fr'
                ? 'En cas d\u2019urgence, appelez le 911 imm\u00e9diatement apr\u00e8s l\u2019administration.'
                : 'In case of emergency, call 911 (U.S.) immediately after administration.'}
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-neutral-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-manrope text-sm text-neutral-500 leading-relaxed">
            {language === 'fr'
              ? 'Cette page offre un acc\u00e8s pratique \u00e0 la vid\u00e9o officielle du fabricant pour Nayzilam\u00ae (midazolam en vaporisateur nasal). MyGuardPod\u2122 n\u2019est pas affili\u00e9 au fabricant et ne fournit pas de conseils m\u00e9dicaux. Suivez toujours les instructions de votre professionnel de la sant\u00e9 et appelez les services d\u2019urgence au besoin.'
              : 'This page provides convenient access to the official manufacturer video for Nayzilam\u00ae (midazolam nasal spray). MyGuardPod\u2122 is not affiliated with the manufacturer and does not provide medical advice. Always follow your healthcare provider\u2019s instructions and call emergency services when required.'}
          </p>
        </div>
      </section>
    </main>
  );
};

export default NayziPage;
